<template>
  <div class="help-center">
    <div class="help-container">
      <!-- 左侧目录导航 -->
      <div class="help-sidebar">
        <div class="sidebar-nav">
          <!-- 只展示有数据的目录 -->
          <div 
            v-for="(category, categoryIndex) in categoriesList" 
            :key="categoryIndex"
            class="nav-category"
          >
            <div 
              class="category-header"
              @click="toggleCategory(categoryIndex)"
            >
              <span class="category-name">{{ category.name }}</span>
              <span class="category-arrow" :class="{ 'expanded': expandedCategories[categoryIndex] }">▼</span>
            </div>
            
            <div class="category-items" v-show="expandedCategories[categoryIndex]">
              <div 
                v-for="(item, itemIndex) in category.items" 
                :key="itemIndex"
                class="nav-item"
                :class="{ 'active': activeItem === `${categoryIndex}_${itemIndex}` }"
                @click="selectItem(categoryIndex, itemIndex, item)"
              >
                <span class="item-icon">📄</span>
                <span class="item-name">{{ item.title }}</span>
              </div>
            </div>
          </div>
        </div>
        

      </div>
      
      <!-- 右侧详情内容 -->
      <div class="help-content">
        <div v-if="currentItem" class="content-area">
          <!-- 文章标题和元信息 -->
          <div class="article-header">
            <h1 class="article-title">{{ currentItem.title }}</h1>
            <div v-if="currentItem.createTime" class="article-meta">
              <span class="publish-time">发布时间：{{ formatDate(currentItem.createTime) }}</span>
            </div>
          </div>
          
          <!-- 文章内容（富文本格式） -->
          <div class="article-content" v-html="currentItem.content"></div>
        </div>
        
        <!-- 默认显示内容 -->
        <div v-else class="content-placeholder">
          <div class="placeholder-icon">📖</div>
          <h2 class="placeholder-title">欢迎使用帮助中心</h2>
          <p class="placeholder-text">请从左侧选择您需要了解的功能或教程</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { getHelpCenter } from '../api/admin'

// 当前选中的项目
const activeItem = ref(null)
const currentItem = ref(null)

// 帮助分类数据
const helpCategories = ref({})

// 展开的分类状态
const expandedCategories = ref({})

// 格式化日期时间
function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}`
}

// 默认演示数据
const defaultHelpData = {
  '测试': [
    {
      articleColumn: "1",
      content: "<div class='rich-text'><h2>测试内容</h2><p>这是一段测试内容，包含富文本格式。</p><ul><li>测试点1</li><li>测试点2</li></ul></div>",
      id: 1,
      remark: "",
      status: "1",
      title: "测试",
      createTime: "2025-09-01T06:25:15.000+00:00"
    }
  ]
}

// 计算属性：将对象转换为数组，并过滤掉空数组的目录
const categoriesList = computed(() => {
  return Object.keys(helpCategories.value)
    .filter(key => helpCategories.value[key] && helpCategories.value[key].length > 0)
    .map(key => ({
      name: key,
      items: helpCategories.value[key]
    }))
})

// 切换分类展开状态
function toggleCategory(categoryIndex) {
  expandedCategories.value = {
    ...expandedCategories.value,
    [categoryIndex]: !expandedCategories.value[categoryIndex]
  }
}

// 选择帮助项目
function selectItem(categoryIndex, itemIndex, item) {
  activeItem.value = `${categoryIndex}_${itemIndex}`
  currentItem.value = item
}

// 在组件挂载时调用API获取帮助中心数据
onMounted(async () => {
  try {
    // 设置页面标题
    document.title = '聚宝铺-帮助中心'
    
    // 调用getHelpCenter API获取数据
    const res = await getHelpCenter()
    console.log('API返回数据:', res)
    
    if (res.code === 200 && res.data) {
      // 使用API返回的数据
      helpCategories.value = res.data
      
      // 如果获取到数据，默认展开第一个分类
      if (categoriesList.value.length > 0) {
        expandedCategories.value[0] = true
      }
    } else {
      // 使用默认演示数据
      console.log('使用默认数据')
      helpCategories.value = defaultHelpData
      // 默认展开第一个分类
      if (categoriesList.value.length > 0) {
        expandedCategories.value[0] = true
      }
    }
  } catch (error) {
    console.error('获取帮助中心数据失败:', error)
    // 出错时使用默认数据
    helpCategories.value = defaultHelpData
    // 默认展开第一个分类
    if (categoriesList.value.length > 0) {
      expandedCategories.value[0] = true
    }
  }
})
</script>

<style scoped>
.help-center {
  min-height: 70vh;
  background: #f7faff;
  padding: 0;
  width: 100%;
}

.help-container {
  width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  gap: 12px;
}

/* 左侧边栏样式 */
.help-sidebar {
  width: 400px;
  min-width: 400px;
  max-width: 400px;
  background: #fff;
  border-radius: 0;
  box-shadow: 2px 0 16px rgba(75, 163, 250, 0.1);
  height: fit-content;
  position: static;
}

.sidebar-header {
  padding: 24px 24px 16px;
  border-bottom: 1px solid #eaf3fb;
}

.sidebar-title {
  color: #333;
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0;
}

.sidebar-nav {
  padding: 0 0;
}

.nav-category {
  margin-bottom: 8px;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 24px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.category-header:hover {
  background-color: #f8faff;
}

.category-name {
  color: #333;
  font-size: 1rem;
  font-weight: 500;
}

.category-arrow {
  color: #4ba3fa;
  font-size: 0.8rem;
  transition: transform 0.3s;
}

.category-arrow.expanded {
  transform: rotate(180deg);
}

.category-items {
  background-color: #f8faff;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 5px 16px 5px 48px;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
}

.nav-item:hover {
  background-color: #eaf3fb;
  color: #2979ff;
}

.nav-item.active {
  background-color: #2979ff;
  color: #fff;
}

.nav-item.active .item-name {
  color: #fff;
}

.item-icon {
  font-size: 1.2rem;
  width: 20px;
  text-align: center;
}

.item-name {
    color: #666;
    font-size: 0.95rem;
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

.sidebar-footer {
  padding: 16px 24px;
  border-top: 1px solid #eaf3fb;
}

/* 右侧内容区域样式 */
.help-content {
  flex: 1;
  background: #fff;
  border-radius: 0;
  box-shadow: -2px 0 16px rgba(75, 163, 250, 0.1);
  min-height: 750px;
}

.content-area {
  padding: 20px;
}

.article-header {
  margin-bottom: 32px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eaf3fb;
}

.article-title {
  color: #333;
  font-size: 1.9rem;
  font-weight: bold;
  line-height: 1.3;
  margin-bottom: 16px;
}

.article-meta {
  color: #666;
  font-size: 0.9rem;
}

.publish-time {
  color: #999;
  display: flex;
  justify-content: center;
}

.article-content {
  line-height: 1.8;
}

.intro-section {
  margin-bottom: 32px;
}

.intro-text {
  color: #333;
  font-size: 1.1rem;
  line-height: 1.8;
}

.screenshot-section {
  margin: 32px 0;
  text-align: center;
}

.screenshot-container {
  position: relative;
  display: inline-block;
  max-width: 100%;
}

.screenshot-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.screenshot-annotations {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.annotation {
  position: absolute;
  z-index: 10;
}

.annotation-arrow {
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 12px solid #ff4444;
  margin: 0 auto;
}

.annotation-text {
  background: #ff4444;
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.9rem;
  white-space: nowrap;
  margin-top: 4px;
}

.features-section {
  margin: 32px 0;
}

.section-title {
  color: #333;
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 16px;
}

.section-text {
  color: #666;
  font-size: 1rem;
  line-height: 1.8;
}

/* 默认显示内容样式 */
.content-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 600px;
  text-align: center;
}

.placeholder-icon {
  font-size: 4rem;
  margin-bottom: 24px;
  opacity: 0.6;
}

.placeholder-title {
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 16px;
}

.placeholder-text {
  color: #666;
  font-size: 1.1rem;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .help-container {
    flex-direction: column;
  }
  
  .help-sidebar {
    position: static;
    flex: none;
    width: 100%;
    min-width: auto;
    max-width: none;
  }
}

@media (max-width: 768px) {
  .help-center {
    padding: 10px 0;
  }
  
  .help-container {
    padding: 0 16px;
    gap: 20px;
  }
  
  .content-area {
    padding: 24px;
  }
  
  .article-title {
    font-size: 1.6rem;
  }
  
  .nav-item {
    padding: 12px 20px 12px 40px;
  }
}
</style>
